import { useEffect, useRef, useState } from 'react'
import { useNotificationStore, type NotificationVariant } from '@/stores/notification-store'

/* `default` uses the muted fill and a primary-tinted border, not popover + the
   plain border: in the light theme popover is pure white on a 96%-light page, so
   the card was white-on-white with a near-invisible edge. The border carries most
   of the work — a 5% fill step alone does not define an edge. */
const VARIANT_STYLES: Record<NotificationVariant, string> = {
  default: 'border-primary/50 bg-muted text-foreground',
  success: 'border-green-600/30 bg-green-950/90 text-green-200',
  error: 'border-red-600/30 bg-red-950/90 text-red-200',
  info: 'border-blue-600/30 bg-blue-950/90 text-blue-200',
  warning: 'border-yellow-600/30 bg-yellow-950/90 text-yellow-200',
}

export function NotificationToast() {
  const notification = useNotificationStore((s) => s.current)
  const clearNotification = useNotificationStore((s) => s.clearNotification)
  const [visible, setVisible] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  useEffect(() => {
    if (!notification) {
      setVisible(false)
      return
    }

    setVisible(true)
    clearTimeout(timerRef.current)

    const duration = notification.autoHideDuration ?? 5000
    if (!notification.persist) {
      timerRef.current = setTimeout(() => {
        setVisible(false)
        setTimeout(clearNotification, 200) // wait for fade-out
      }, duration)
    }

    return () => clearTimeout(timerRef.current)
  }, [notification, clearNotification])

  if (!notification) return null

  const style = VARIANT_STYLES[notification.variant] ?? VARIANT_STYLES.default

  return (
    <div
      // `--debug-sidebar-inset` is only ever set by the dev debug sidebar, which
      // is stripped from production builds; elsewhere this is plain `right: 1rem`.
      style={{ right: 'calc(1rem + var(--debug-sidebar-inset, 0px))' }}
      className={
        'pointer-events-none fixed bottom-4 z-600 max-w-sm rounded-md border px-4 py-3 text-sm shadow-lg transition-all duration-200 ' +
        style +
        (visible ? ' translate-y-0 opacity-100' : ' translate-y-2 opacity-0')
      }
    >
      <div className="flex items-center justify-between gap-3">
        <span>{notification.message}</span>
        <div className="flex items-center gap-2">
          {notification.action && (
            <button
              onClick={() => {
                notification.action!.onClick()
                // Bypass clearNotification to avoid firing onClear
                useNotificationStore.setState({ current: null })
                setVisible(false)
              }}
              className="pointer-events-auto shrink-0 font-medium underline underline-offset-2 hover:opacity-80"
            >
              {notification.action.label}
            </button>
          )}
          <button
            onClick={() => {
              setVisible(false)
              setTimeout(clearNotification, 200)
            }}
            className="pointer-events-auto shrink-0 opacity-60 hover:opacity-100"
          >
            <svg
              width={12}
              height={12}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  )
}
